"use client";

/**
 * Widget-specific transaction hook for signing and sending via bridge adapter.
 *
 * Flow:
 * 1. Sign transaction via BridgeWalletAdapter.signTransaction() (postMessage to parent)
 * 2. Broadcast signed tx via our RPC proxy (/api/rpc?chain=solana)
 * 3. Poll for confirmation with readable error handling
 *
 * Returns the same interface as useParaTransactions for interchangeability.
 * Used only when WidgetWalletProvider is mounted (widget routes in iframe).
 */

import { useCallback, useMemo } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { Connection, VersionedTransaction, Transaction } from "@solana/web3.js";

/** Matches useParaTransactions return type */
export interface WidgetTransactionsResult {
  onSendTransaction: (
    transaction: VersionedTransaction | Transaction
  ) => Promise<string>;
}

/** Confirmation polling settings */
const MAX_CONFIRMATION_RETRIES = 30;
const CONFIRMATION_RETRY_DELAY_MS = 2000;

/**
 * Get the RPC proxy URL (same pattern as useParaTransactions, useParaSolana, etc.)
 */
function getRpcProxyUrl(): string {
  return typeof window !== "undefined"
    ? `${window.location.origin}/api/rpc?chain=solana`
    : "https://api.mainnet-beta.solana.com";
}

export function useWidgetTransactions(): WidgetTransactionsResult {
  const { signTransaction, connected, publicKey } = useWallet();

  // Create connection to our RPC proxy (NOT the public clusterApiUrl)
  const connection = useMemo(
    () =>
      new Connection(getRpcProxyUrl(), {
        commitment: "confirmed",
      }),
    []
  );

  const onSendTransaction = useCallback(
    async (
      transaction: VersionedTransaction | Transaction
    ): Promise<string> => {
      // Validate wallet state
      if (!connected || !publicKey) {
        throw new Error(
          "Wallet not connected. Please connect your wallet and try again."
        );
      }

      if (!signTransaction) {
        throw new Error(
          "Wallet does not support transaction signing. Please try a different wallet."
        );
      }

      console.log("[Widget Bridge] Signing transaction via bridge adapter...");

      // BLOCKHASH NOTE: For VersionedTransaction (which LiFi uses), the blockhash
      // is baked into the message at LiFi API call time and cannot be refreshed
      // after deserialization. This matches useParaTransactions behavior, which
      // only refreshes blockhash for legacy transactions (!isVersioned).
      //
      // Bridge latency (postMessage round-trip + human Phantom approval) makes
      // blockhash expiry slightly more likely than the main path. If the blockhash
      // expires (~60-90s), the user will see "Transaction expired" and can retry.
      // Solana blockhash refresh for VersionedTransaction would require rebuilding
      // the entire transaction, which risks breaking instruction integrity.

      // Step 1: Sign via bridge adapter (postMessage to parent window)
      let signedTransaction: VersionedTransaction | Transaction;
      try {
        signedTransaction = await signTransaction(transaction);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown signing error";
        // User rejection or bridge failure - surface readable message
        if (message.toLowerCase().includes("user rejected")) {
          throw new Error("Transaction cancelled by user.");
        }
        throw new Error(`Failed to sign transaction: ${message}`);
      }

      console.log("[Widget Bridge] Transaction signed, broadcasting...");

      // Step 2: Broadcast via our RPC proxy
      let signature: string;
      try {
        signature = await connection.sendRawTransaction(
          signedTransaction.serialize(),
          {
            skipPreflight: false,
            preflightCommitment: "confirmed",
          }
        );
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Unknown broadcast error";
        // Parse common RPC errors into readable messages
        if (message.includes("insufficient funds")) {
          throw new Error(
            "Insufficient funds for this transaction. Please check your balance."
          );
        }
        if (message.includes("blockhash not found")) {
          throw new Error(
            "Transaction expired. Please try again."
          );
        }
        throw new Error(`Failed to send transaction: ${message}`);
      }

      console.log("[Widget Bridge] Broadcast successful, confirming:", signature);

      // Step 3: Poll for confirmation
      for (let attempt = 0; attempt < MAX_CONFIRMATION_RETRIES; attempt++) {
        try {
          const { value } = await connection.getSignatureStatuses([signature]);
          const status = value[0];

          if (
            status?.confirmationStatus === "confirmed" ||
            status?.confirmationStatus === "finalized"
          ) {
            // Transaction landed in a block
            if (status.err) {
              // On-chain failure - parse error if possible
              const errStr = JSON.stringify(status.err);
              if (errStr.includes("InsufficientFunds")) {
                throw new Error(
                  "Transaction failed: insufficient funds for fees."
                );
              }
              throw new Error(`Transaction failed on-chain: ${errStr}`);
            }

            console.log("[Widget Bridge] Transaction confirmed:", signature);
            return signature;
          }

          if (status?.err) {
            throw new Error(
              `Transaction failed: ${JSON.stringify(status.err)}`
            );
          }
        } catch (error) {
          // Only throw if it's our own error (not RPC timeout)
          if (
            error instanceof Error &&
            error.message.startsWith("Transaction failed")
          ) {
            throw error;
          }
          // RPC error during polling - continue retrying
          console.warn(
            `[Widget Bridge] Confirmation poll attempt ${attempt + 1} failed:`,
            error
          );
        }

        // Not confirmed yet - wait and retry
        if (attempt < MAX_CONFIRMATION_RETRIES - 1) {
          await new Promise((resolve) =>
            setTimeout(resolve, CONFIRMATION_RETRY_DELAY_MS)
          );
        }
      }

      // Timeout after all retries exhausted
      // The transaction may still confirm later, but we can't wait forever
      throw new Error(
        `Transaction confirmation timed out after ${MAX_CONFIRMATION_RETRIES * CONFIRMATION_RETRY_DELAY_MS / 1000} seconds. ` +
        `Your transaction may still complete - check your wallet or explorer for status. ` +
        `Signature: ${signature.slice(0, 20)}...`
      );
    },
    [signTransaction, connected, publicKey, connection]
  );

  return { onSendTransaction };
}
